<template>
  <div>
    <h3 class="mt-4 page-header-2">Actual Times</h3>
    <div class="overflow-auto">
      <Table class="mt-2 job-times-table">
        <thead>
          <tr>
            <th>Scheduled</th>
            <th>Appointment #</th>
            <th>Status</th>
            <th>Service Category</th>
            <th>Tech and Crew Members</th>
            <th class="duration-time-header">Pre-Work</th>
            <th class="duration-time-header">Travel</th>
            <th class="duration-time-header">Work</th>
            <th class="duration-time-header">Duration</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="appointment in job.appointments"
            :key="appointment.id"
            :class="{ 'table-hint-cell': !isAppointmentFinished(appointment) }"
          >
            <td>{{ formatTableDate(appointment.scheduled_time) }}</td>
            <td>
              <span class="link" @click="openAppt(appointment.id)">{{
                appointment.id
              }}</span>
            </td>
            <td>{{ statusTag(appointment.status) }}</td>
            <td class="service-cell">
              {{ appointment.service_category.name }}
            </td>
            <td style="max-width: 250px">
              <div>
                <truncate-text v-if="getTechLinkURL(appointment.technician)">
                  <Link
                    :to="getTechLinkURL(appointment.technician)"
                    :text="fullName(appointment.technician)"
                  />
                </truncate-text>
                <span v-else>--</span>
                <div
                  v-for="crew in appointment.crew_members"
                  :key="`${crew.id}-name`"
                  class="mt-1"
                >
                  {{ crew.full_name }}
                </div>
              </div>
            </td>
            <td class="text-right">
              <div>
                <div>
                  <minutes-label
                    :minutes="appointment.pre_work_minutes"
                    :not-available="!job.job_times_valid"
                    :default-text="missingDataTooltip"
                    :not-available-text="notAvailableTooltip"
                  />
                </div>
                <div
                  v-for="crew in appointment.crew_members"
                  :key="`${crew.id}-pre_work`"
                  class="mt-1"
                >
                  <minutes-label
                    :minutes="crew.pre_work_minutes"
                    :not-available="!job.job_times_valid"
                    :default-text="missingDataTooltip"
                    :not-available-text="notAvailableTooltip"
                  />
                </div>
              </div>
            </td>
            <td class="text-right">
              <div>
                <div>
                  <minutes-label
                    :minutes="appointment.travel_minutes"
                    :not-available="!job.job_times_valid"
                    :default-text="missingDataTooltip"
                    :not-available-text="notAvailableTooltip"
                  />
                </div>
                <div
                  v-for="crew in appointment.crew_members"
                  :key="`${crew.id}-travel`"
                  class="mt-1"
                >
                  <minutes-label
                    :minutes="crew.travel_minutes"
                    :not-available="!job.job_times_valid"
                    :default-text="missingDataTooltip"
                    :not-available-text="notAvailableTooltip"
                  />
                </div>
              </div>
            </td>
            <td class="text-right">
              <div>
                <div>
                  <minutes-label
                    :minutes="appointment.work_minutes"
                    :not-available="!job.job_times_valid"
                    :default-text="missingDataTooltip"
                    :not-available-text="notAvailableTooltip"
                  />
                </div>
                <div
                  v-for="crew in appointment.crew_members"
                  :key="`${crew.id}-work`"
                  class="mt-1"
                >
                  <minutes-label
                    :minutes="crew.work_minutes"
                    :not-available="!job.job_times_valid"
                    :default-text="missingDataTooltip"
                    :not-available-text="notAvailableTooltip"
                  />
                </div>
              </div>
            </td>
            <td class="text-right">
              <div>
                <div>
                  <minutes-label
                    :minutes="appointment.duration_minutes"
                    :not-available="!job.job_times_valid"
                    :default-text="missingDataTooltip"
                    :not-available-text="notAvailableTooltip"
                  />
                </div>
                <div
                  v-for="crew in appointment.crew_members"
                  :key="`${crew.id}-duration`"
                  class="mt-1"
                >
                  <minutes-label
                    :minutes="crew.duration_minutes"
                    :not-available="!job.job_times_valid"
                    :default-text="missingDataTooltip"
                    :not-available-text="notAvailableTooltip"
                  />
                </div>
              </div>
            </td>
          </tr>
        </tbody>
        <tfoot>
          <tr>
            <td>Completed Totals</td>
            <td></td>
            <td></td>
            <td></td>
            <td></td>
            <td class="text-right">
              <minutes-label
                :minutes="job.pre_work_minutes"
                :not-available="!job.job_times_valid"
                :default-text="missingDataTooltip"
                :not-available-text="notAvailableTooltip"
              />
            </td>
            <td class="text-right">
              <minutes-label
                :minutes="job.travel_minutes"
                :not-available="!job.job_times_valid"
                :default-text="missingDataTooltip"
                :not-available-text="notAvailableTooltip"
              />
            </td>
            <td class="text-right">
              <minutes-label
                :minutes="job.work_minutes"
                :not-available="!job.job_times_valid"
                :default-text="missingDataTooltip"
                :not-available-text="notAvailableTooltip"
              />
            </td>
            <td class="text-right">
              <minutes-label
                :minutes="job.total_minutes"
                :not-available="!job.job_times_valid"
                :default-text="missingDataTooltip"
                :not-available-text="notAvailableTooltip"
              />
            </td>
          </tr>
        </tfoot>
      </Table>
    </div>
  </div>
</template>

<script>
import { mapState } from "vuex";
import { EventBus } from "@/lib/event_bus.js";
import { updateQueryParam } from "@/lib/url.js";

import FeatureFlag from "@/components/FeatureFlag.vue";
import Link from "@/components/Link.vue";
import MinutesLabel from "@/components/MinutesLabel.vue";
import Table from "@/components/Table.vue";
import TruncateText from "@/components/TruncateText.vue";
import CONSTANTS from "@/Constants.js";
const { NO_JOB_TIME_TOOLTIP_TEXT, NOT_AVAILABLE_JOB_TIME_TOOLTIP_TEXT } =
  CONSTANTS;

export default {
  components: {
    FeatureFlag,
    Table,
    Link,
    MinutesLabel,
    TruncateText
  },
  computed: {
    ...mapState(["job"]),
    missingDataTooltip() {
      return NO_JOB_TIME_TOOLTIP_TEXT;
    },
    notAvailableTooltip() {
      return NOT_AVAILABLE_JOB_TIME_TOOLTIP_TEXT;
    }
  },
  methods: {
    async openAppt(apptId) {
      await updateQueryParam("appointment_id", apptId);
      EventBus.$emit("set-selected-tab", "Appointments");
    },
    statusTag(status) {
      return CONSTANTS.APPOINTMENT_STATUS_MAP[status];
    },
    getTechLinkURL(tech) {
      return tech ? this.technicianShowPath(tech.user_id) : "";
    },
    isAppointmentFinished({ status }) {
      return status === "completed" || status === "cancelled";
    }
  }
};
</script>

<style lang="scss" scoped>
table > tbody > tr.table-hint-cell > td {
  font-style: italic;
  color: var(--secondary-color) !important;
}

.service-cell {
  max-width: 350px;
  white-space: initial;
}
</style>
